import { useState, FC, useMemo, Fragment } from 'react';

import Gradinet from './Gradient';
import Solid from './Solid';

import { PopupTabs, PopupTabsBody, PopupTabsBodyItem } from '../PopupTab';
import { ButtonControls } from '../../../../../ui/ButtonControls';
import { useTranslation } from '../../../../../lib/i18n/TranslationContext';
import { getIndexActiveTag } from './helper';

import { IPropsMain } from './types';

import { DEFAULT_COLORS } from './constants';

const ColorPicker: FC<IPropsMain> = ({
  value = '#ffffff',
  format = 'rgb',
  gradient = false,
  solid = true,
  debounceMS = 300,
  debounce = true,
  showAlpha = true,
  showInputs = true,
  showGradientResult = true,
  showGradientStops = true,
  showGradientMode = true,
  showGradientAngle = true,
  showGradientPosition = true,
  allowAddGradientStops = true,
  popupWidth = 267,
  colorBoardHeight = 120,
  defaultColors = DEFAULT_COLORS,
  defaultActiveTab,
  onChangeTabs,
  onChange = () => ({}),
  onDefaultColorSelect = () => ({}),
  labels = {}
}) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<string>(
    defaultActiveTab ?? getIndexActiveTag(value, solid, gradient)
  );

  const onChangeSolid = (value: string) => {
    onChange(value);
  };

  const onChangeGradient = (value: string) => {
    onChange(value);
  };

  const onChangeTab = (tab: string) => {
    setActiveTab(tab);
    if (typeof onChangeTabs === 'function' && !!onChangeTabs) {
      onChangeTabs(tab);
    }
  };

  // Rótulos traduzidos pela lib (chave = pt-BR, como no resto do empreender-ui). A prop `labels`
  // continua ganhando de tudo, para quem quiser texto próprio.
  const tabs: { name: string; label: string }[] = useMemo(() => {
    return [
      solid && {
        name: 'solid',
        label: labels.solid ?? t('Sólida')
      },
      gradient && {
        name: 'gradient',
        label: labels.gradient ?? t('Gradiente')
      }
    ].filter(Boolean) as { name: string; label: string }[];
  }, [solid, gradient, labels, t]);


  return (
    <PopupTabs activeTab={activeTab} popupWidth={popupWidth}>
      {tabs?.length > 1 ? (
        <ButtonControls
          value={activeTab}
          onChange={(tab) => onChangeTab(String(tab))}
          className='mb-3'
        >
          {tabs?.map((tab) => (
            <ButtonControls.Item key={tab.name} value={tab.name}>
              {tab.label}
            </ButtonControls.Item>
          ))}
        </ButtonControls>
      ) : (
        <Fragment />
      )}
      <PopupTabsBody>
        {tabs?.map((tab) => {
          switch (tab.name) {
            case 'solid':
              return (
                <PopupTabsBodyItem key={tab.name} tabName='solid'>
                  <Solid
                    onChange={onChangeSolid}
                    onDefaultColorSelect={onDefaultColorSelect}
                    value={value}
                    format={format}
                    defaultColors={defaultColors}
                    debounceMS={debounceMS}
                    debounce={debounce}
                    showAlpha={showAlpha}
                    showInputs={showInputs}
                    colorBoardHeight={colorBoardHeight}
                    labels={labels}
                  />
                </PopupTabsBodyItem>
              );

            case 'gradient':
              return (
                <PopupTabsBodyItem key={tab.name} tabName='gradient'>
                  <Gradinet
                    onChange={onChangeGradient}
                    onDefaultColorSelect={onDefaultColorSelect}
                    value={value}
                    format={format}
                    defaultColors={defaultColors}
                    debounceMS={debounceMS}
                    debounce={debounce}
                    showAlpha={showAlpha}
                    showInputs={showInputs}
                    showGradientResult={showGradientResult}
                    showGradientStops={showGradientStops}
                    showGradientMode={showGradientMode}
                    showGradientAngle={showGradientAngle}
                    showGradientPosition={showGradientPosition}
                    allowAddGradientStops={allowAddGradientStops}
                    colorBoardHeight={colorBoardHeight}
                    labels={labels}
                  />
                </PopupTabsBodyItem>
              );

            default:
              return <Fragment />;
          }
        })}
      </PopupTabsBody>
    </PopupTabs>
  );
};

export default ColorPicker;
